// Fills in and opens the "you met us at an event" dialog when a visitor lands
// with the query params printed on our business cards:
//
//   ?met=Drew%20Ulmer&at=Rails%20World%202026&with=Kit%20Ryan,Cassey%20Lottman
//
// Every name accepts full names or frontmatter nicknames (see met_you.rb).
// Everything the dialog needs is already on the page: one inert <template> per
// team member, each holding that person's optimized avatar markup.

const SIZE_CLASSES = [
  'size-xs', 'size-sm', 'size-default', 'size-lg', 'size-xl',
  'size-2xl', 'size-3xl', 'size-4xl', 'size-5xl', 'size-6xl',
];

const PRIMARY_SIZE = 'size-4xl';
// Upper bound on crowd avatars; anything past this is dropped from the card's
// `with` list. `crowdSizeFor` shrinks the avatars as the group grows so even a
// full-team card fits inside the dialog.
const MAX_CROWD = 12;

// Mirrors `slug_for` in met_you.rb.
const normalize = (value) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Rough initials for names that don't match any team member template. This
// intentionally isn't identical to `initials` in ui/avatar.rb — it just has to
// produce something reasonable for a typo'd or unknown name.
const initialsFor = (name) =>
  name.replace(/[a-z]+/g, '').replace(/\s+/g, '').slice(0, 2) || 'US';

const fillTokens = (template, tokens) =>
  Object.keys(tokens).reduce(
    (text, token) => text.split(`{${token}}`).join(tokens[token]),
    template
  );

const formatList = (names) => {
  if (typeof Intl === 'undefined' || !Intl.ListFormat) return names.join(', ');

  const locale = document.documentElement.lang || 'en';
  return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(names);
};

const crowdSizeFor = (count) => {
  if (count <= 5) return 'size-xl';
  if (count <= 8) return 'size-lg';
  return 'size-default';
};

const init = () => {
  const dialog = document.querySelector('[data-met-you]');
  if (!dialog || typeof dialog.showModal !== 'function') return;

  const params = new URLSearchParams(window.location.search);
  const met = (params.get('met') || '').trim();
  const at = (params.get('at') || '').trim();
  if (!met || !at) return;

  const strings = JSON.parse(dialog.dataset.metYouStrings);

  const templates = new Map();
  dialog.querySelectorAll('template[data-met-you-member]').forEach((template) => {
    // Register the canonical slug plus any frontmatter nicknames, so both
    // `met=Drew%20Ulmer` and `met=Drew` resolve to the same person.
    templates.set(template.dataset.metYouMember, template);
    (template.dataset.metYouAliases || '')
      .split(/\s+/)
      .filter(Boolean)
      .forEach((alias) => templates.set(alias, template));
  });

  // The name we print: the team member's canonical name when we know them,
  // otherwise whatever the link said.
  const displayName = (name) => {
    const template = templates.get(normalize(name));
    return template ? template.dataset.metYouName : name;
  };

  // The primary person's social links, from the same template as their avatar.
  const linksFor = (name) => {
    const template = templates.get(normalize(name));
    if (!template) return [];

    try {
      return JSON.parse(template.dataset.metYouLinks || '[]');
    } catch (error) {
      return [];
    }
  };

  const chipFor = (link) => {
    const anchor = document.createElement('a');
    anchor.className = 'unabridged-button type-outline size-small pill';
    anchor.href = link.href;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';

    const icon = document.createElement('ion-icon');
    icon.className = 'unabridged-icon';
    icon.setAttribute('name', link.icon);

    anchor.appendChild(icon);
    anchor.appendChild(document.createTextNode(link.label));
    return anchor;
  };

  const avatarFor = (name, sizeClass) => {
    const template = templates.get(normalize(name));
    let avatar;

    if (template) {
      avatar = template.content.firstElementChild.cloneNode(true);
    } else {
      avatar = document.createElement('span');
      avatar.className = 'unabridged-avatar shape-squircle';
      const letter = document.createElement('div');
      letter.className = 'letter';
      letter.textContent = initialsFor(name);
      avatar.appendChild(letter);
    }

    SIZE_CLASSES.forEach((size) => avatar.classList.remove(size));
    avatar.classList.add(sizeClass);
    return avatar;
  };

  // Canonicalize before filtering: a card can mix full names and nicknames
  // (`with=Will,Bill` beside `met=William M. Riley`), and only the canonical
  // forms will collapse to one person here.
  const primary = displayName(met);
  const others = (params.get('with') || '')
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean)
    .map(displayName)
    .filter((name, index, all) => all.indexOf(name) === index)
    .filter((name) => normalize(name) !== normalize(primary))
    .slice(0, MAX_CROWD);

  const tokens = { name: primary, event: at, others: formatList(others) };

  dialog.querySelector('[data-met-you-title]').textContent =
    fillTokens(strings.heading, tokens);
  dialog.querySelector('[data-met-you-body]').textContent =
    fillTokens(others.length ? strings.body_with_team || strings.body : strings.body, tokens);

  dialog.querySelector('[data-met-you-primary]').appendChild(avatarFor(met, PRIMARY_SIZE));

  // Only the person on the card gets links: the crowd is there as faces, and a
  // dozen more buttons would bury the two that matter.
  const links = linksFor(met);
  if (links.length) {
    dialog.querySelector('[data-met-you-links-label]').textContent =
      fillTokens(strings.links, tokens);
    const list = dialog.querySelector('[data-met-you-links-list]');
    links.forEach((link) => list.appendChild(chipFor(link)));
    dialog.querySelector('[data-met-you-links]').hidden = false;
  }

  const crowd = dialog.querySelector('[data-met-you-crowd]');
  const crowdSize = crowdSizeFor(others.length);
  others.forEach((name, index) => {
    // Fan the crowd out from the middle: -1.5, -0.5, 0.5, 1.5 for four of them.
    const offset = index - (others.length - 1) / 2;
    const avatar = avatarFor(name, crowdSize);
    avatar.style.setProperty('--offset', `${offset}`);
    avatar.style.setProperty('--distance', `${Math.abs(offset)}`);
    crowd.appendChild(avatar);
  });

  dialog.classList.toggle('met-you--solo', others.length === 0);
  dialog.showModal();

  // showModal() hands focus to the first focusable control, which here is the
  // close button — so the dialog opens with a focus ring drawn around the X.
  // Park focus on the dialog itself instead (hence its tabindex="-1"): still
  // inside the modal, so Esc and the screen-reader announcement both work, but
  // nothing is outlined until the visitor actually tabs.
  dialog.focus();
};

// The bundle loads as a deferred module today, so DOMContentLoaded hasn't
// fired yet — but init directly if that ever stops being true.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
